<script setup>

import {computed, onMounted, reactive, ref, watch} from "vue";
import {useAdminStore} from "@/store/admin";
import {useToast} from "vue-toast-notification";
import MainPreloader from "@/components/shared/MainPreloader.vue";
import EmptyCard from "@/components/shared/EmptyCard.vue";
import AdminModal from "@/components/AdminView/AdminModal.vue";
import ActionButton from "@/components/ui/ActionButton.vue";
import FormInput from "@/components/ui/FormInput.vue";
import CheckBox from "@/components/ui/CheckBox.vue";

const adminStore = useAdminStore();
const toast = useToast();

const items = computed(() => adminStore.promoCodes);
const products = computed(() => adminStore.products);
const FILTER_STORAGE_KEY = 'adminPromotionsFilter';
const filter = ref(localStorage.getItem(FILTER_STORAGE_KEY) || 'active');
watch(filter, value => localStorage.setItem(FILTER_STORAGE_KEY, value));
const visibleItems = computed(() => filter.value === 'all'
    ? items.value
    : items.value.filter(isCurrentlyActive));
const showForm = ref(false);
const saving = ref(false);
const generatingCode = ref(false);
const editing = ref(null);
const form = reactive(defaultForm());

function defaultForm() {
  return {
    code: "",
    description: "",
    discount_percent: 10,
    starts_at: toLocalDateTimeValue(new Date()),
    ends_at: "",
    is_active: true,
    product_id: "",
  };
}

function resetForm() {
  Object.assign(form, defaultForm());
}

function formatDate(value) {
  return value ? new Date(value).toLocaleString('ru-RU') : '—';
}

function toLocalDateTimeValue(value) {
  if (!value) return '';
  const date = new Date(value);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
}

function toISOString(value) {
  return value ? new Date(value).toISOString() : null;
}

function statusTitle(item) {
  const now = Date.now();
  if (!item.is_active) return 'Отключён';
  if (item.starts_at && new Date(item.starts_at).getTime() > now) return 'Не начался';
  if (item.ends_at && new Date(item.ends_at).getTime() <= now) return 'Истёк';
  return 'Активен';
}

function isCurrentlyActive(item) {
  return statusTitle(item) === 'Активен';
}

function openCreate() {
  resetForm();
  editing.value = null;
  showForm.value = true;
}

function openEdit(item) {
  editing.value = item;
  Object.assign(form, {
    code: item.code || '',
    description: item.description || '',
    discount_percent: item.discount_percent,
    starts_at: toLocalDateTimeValue(item.starts_at),
    ends_at: toLocalDateTimeValue(item.ends_at),
    is_active: !!item.is_active,
    product_id: item.product_id || '',
  });
  showForm.value = true;
}

function cancelForm() {
  showForm.value = false;
  editing.value = null;
  resetForm();
}

async function generateCodeHandler() {
  generatingCode.value = true;
  try {
    const code = await adminStore.generatePromoCode();
    if (!code) {
      toast.error(adminStore.error || "Не удалось сгенерировать промокод");
      return;
    }
    form.code = code;
  } finally {
    generatingCode.value = false;
  }
}

async function saveHandler() {
  const code = (form.code || '').trim();
  if (!code) {
    toast.error("Укажите код промокода");
    return;
  }
  const percent = Number(form.discount_percent);
  if (!Number.isInteger(percent) || percent < 1 || percent > 99) {
    toast.error("Размер скидки должен быть целым числом от 1 до 99");
    return;
  }
  const startsAt = toISOString(form.starts_at);
  const endsAt = toISOString(form.ends_at);
  if (!startsAt) {
    toast.error("Укажите дату начала акции");
    return;
  }
  if (endsAt && endsAt <= startsAt) {
    toast.error("Дата окончания должна быть позже даты начала");
    return;
  }
  const payload = {
    code,
    description: (form.description || '').trim(),
    discount_percent: percent,
    starts_at: startsAt,
    ends_at: endsAt,
    is_active: !!form.is_active,
    product_id: form.product_id || null,
  };
  saving.value = true;
  try {
    const saved = editing.value
        ? await adminStore.updatePromoCode(editing.value.id, payload)
        : await adminStore.createPromoCode(payload);
    if (!saved) {
      toast.error(adminStore.error || "Не удалось сохранить промокод");
      return;
    }
    toast.success(editing.value ? "Промокод обновлён" : "Промокод создан");
    cancelForm();
    await load();
  } finally {
    saving.value = false;
  }
}

async function deleteHandler(item) {
  if (!confirm(`Удалить промокод "${item.code}"?`)) return;
  const deleted = await adminStore.deletePromoCode(item.id);
  if (!deleted) {
    toast.error(adminStore.error || "Не удалось удалить промокод");
    return;
  }
  await load();
  toast.success("Промокод удалён");
}

async function load() {
  await Promise.all([
    adminStore.loadPromoCodes(),
    adminStore.loadProductsData({page: 1, per_page: 100}),
  ]);
  if (adminStore.error) {
    toast.error(adminStore.error);
    return;
  }
}

onMounted(load);

</script>

<template>
  <div class="adminCrud">
    <MainPreloader v-if="adminStore.loading" position="fixed" :size="80" :stroke="3"/>

    <div class="adminCrud__toolbar">
      <div class="adminCrud__toolbarTitle">Промоакции</div>
      <div class="adminCrud__toolbarActions">
        <select v-model="filter" class="adminCrud__select adminCrud__select--filter" aria-label="Фильтр промоакций">
          <option value="active">Действующие</option>
          <option value="all">Все</option>
        </select>
        <ActionButton style="background-color: #6FC062; color: #212226;" @buttonClick="openCreate">
          <template #caption>Добавить промоакцию</template>
        </ActionButton>
      </div>
    </div>

    <AdminModal :visible="showForm" :title="editing ? 'Редактирование промоакции' : 'Новая промоакция'" @close="cancelForm">
      <div class="adminCrud__formGrid">
        <div class="adminCrud__field">
          <div class="adminCrud__fieldLabel">
            <label class="adminCrud__label">Код промокода</label>
            <button
                class="adminCrud__generateButton"
                type="button"
                :disabled="generatingCode"
                @click="generateCodeHandler"
            >
              {{ generatingCode ? 'Генерация...' : 'Сгенерировать' }}
            </button>
          </div>
          <FormInput v-model="form.code" placeholder="Например, SALE10"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Размер скидки, %</label>
          <FormInput v-model="form.discount_percent" type="number"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Дата с</label>
          <FormInput v-model="form.starts_at" type="datetime-local"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Дата по (необязательно)</label>
          <FormInput v-model="form.ends_at" type="datetime-local"/>
        </div>
        <div class="adminCrud__field adminCrud__field--wide">
          <label class="adminCrud__label">Описание</label>
          <FormInput v-model="form.description" rows="3" placeholder="Условия или назначение акции"/>
        </div>
        <div class="adminCrud__field adminCrud__field--wide">
          <label class="adminCrud__label">Товар, на который действует промокод</label>
          <select v-model="form.product_id" class="adminCrud__select">
            <option value="">Все товары</option>
            <option v-for="product in products" :key="product.id" :value="product.id">
              {{ product.nm || product.id }}
            </option>
          </select>
        </div>
        <div class="adminCrud__field adminCrud__field--wide">
          <CheckBox v-model="form.is_active" text="Промокод активен"/>
        </div>
      </div>
      <template #footer>
        <ActionButton @buttonClick="cancelForm">
          <template #caption>Отмена</template>
        </ActionButton>
        <ActionButton
            :disabled="saving"
            style="background-color: #6FC062; color: #212226;"
            @buttonClick="saveHandler"
        >
          <template #caption>Сохранить</template>
        </ActionButton>
      </template>
    </AdminModal>

    <EmptyCard v-if="!visibleItems.length && !adminStore.loading"/>
    <table v-else-if="visibleItems.length" class="adminTable">
      <thead>
      <tr>
        <th>Код</th>
        <th>Действия</th>
        <th>Описание</th>
        <th>Скидка</th>
        <th>Дата с</th>
        <th>Дата по</th>
        <th>Товар</th>
        <th>Статус</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="item in visibleItems" :key="item.id">
        <td>{{ item.code }}</td>
        <td class="adminCrud__actions">
          <button class="adminCrud__actionButton" title="Изменить" @click="openEdit(item)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button class="adminCrud__actionButton adminCrud__actionButton--danger" title="Удалить" @click="deleteHandler(item)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6h18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M10 11v6M14 11v6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </td>
        <td>{{ item.description || '—' }}</td>
        <td>{{ item.discount_percent }}%</td>
        <td>{{ formatDate(item.starts_at) }}</td>
        <td>{{ item.ends_at ? formatDate(item.ends_at) : 'Бессрочно' }}</td>
        <td>{{ item.product_name || 'Все товары' }}</td>
        <td>{{ statusTitle(item) }}</td>
      </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped lang="scss">
.adminCrud {
  display: flex;
  flex-direction: column;
  gap: 14px;

  &__toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__toolbarTitle {
    font-size: 20px;
    font-weight: 600;
    color: var(--cd-text);
  }

  &__toolbarActions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__formGrid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @include respond-to('md') {
      grid-template-columns: 1fr;
    }
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    &--wide {
      grid-column: 1 / -1;
    }
  }

  &__label {
    color: var(--cd-gray-alt);
    font-size: 13px;
  }

  &__fieldLabel {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  &__generateButton {
    border: 1px solid var(--cd-base-green);
    border-radius: 8px;
    padding: 4px 8px;
    background: transparent;
    color: var(--cd-base-green);
    cursor: pointer;
    font: inherit;
    font-size: 12px;

    &:hover:not(:disabled) {
      background: var(--cd-base-green);
      color: #ffffff;
    }

    &:disabled {
      cursor: default;
      opacity: 0.5;
    }
  }

  &__select {
    width: 100%;
    height: 48px;
    border-radius: 20px;
    padding: 5px 20px;
    background: var(--cd-field-bg);
    color: var(--cd-field-color);
    border: 1px solid var(--cd-field-border);
    font-size: 14px;
    font-family: var(--cd-default-font);

    &:focus {
      outline: none;
      border-color: var(--cd-base-green);
      box-shadow: 0 0 5px rgba(98, 192, 100, 0.3);
    }

    &--filter {
      width: auto;
      min-width: 145px;
    }
  }

  &__actions {
    vertical-align: middle;
    white-space: nowrap;
    font-size: 0;

    .adminCrud__actionButton + .adminCrud__actionButton {
      margin-left: 8px;
    }
  }

  &__actionButton {
    background: transparent;
    border: 1px solid var(--cd-base-green);
    color: var(--cd-base-green);
    border-radius: 8px;
    padding: 6px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;

    &:hover {
      background: var(--cd-base-green);
      color: #ffffff;
    }

    &--danger {
      border-color: var(--cd-danger);
      color: var(--cd-danger);

      &:hover {
        background: var(--cd-danger);
        color: #ffffff;
      }
    }
  }
}

.adminTable {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 8px;
  text-align: left;

  @include respond-to('md') {
    display: block;
    overflow-x: auto;
  }

  th {
    color: var(--cd-text-muted);
    font-size: 12px;
    font-weight: 400;
    padding: 0 16px 4px;
  }

  td {
    background: var(--cd-card-bg);
    padding: 12px 16px;
    font-size: 14px;
    color: var(--cd-text);

    &:first-child {
      border-radius: 12px 0 0 12px;
    }

    &:last-child {
      border-radius: 0 12px 12px 0;
    }
  }
}
</style>
